import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DateTime } from "luxon";

import { settingsAjax } from "../../ajax/SettingsAjax";
import { setShiftBoundaries, setShiftEditability, setShiftEditabilityDataLoading } from "../../state/reducers/shiftReducer";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { useTimezone } from "../Providers/TimezoneProvider";
import { RootState } from "../../state/store";
import { shiftEnd, shiftStart } from "../../offline/shiftBands";

export default function useShiftBoundarySync() {
  const dispatch = useDispatch();
  const { timezone, isTimezoneLoading } = useTimezone();
  const selectedDateStr = useSelector((state: RootState) => state.shift.selectedDate);
  const selectedShift = useSelector((state: RootState) => state.shift.selectedShift);
  const latestShiftDate = useSelector((state: RootState) => state.shift.latestShiftDate);
  const latestShift = useSelector((state: RootState) => state.shift.latestShift);
  const provisionalShiftDate = useSelector((state: RootState) => state.shift.provisionalShiftDate);
  const provisionalShift = useSelector((state: RootState) => state.shift.provisionalShift);
  const [shiftBoundaryFetchError, setShiftBoundaryFetchError] = useState<string | null>(null);
  const [shiftEditabilityFetchError, setShiftEditabilityFetchError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedDateStr || !selectedShift || isTimezoneLoading || !timezone) return;

    const selectedDate = DateTime.fromISO(selectedDateStr)
      .toLocal()
      .setZone(timezone, { keepLocalTime: true })
      .startOf("day");
    const backendCode = selectedShift === "DAY" ? "D" : "N";

    setShiftBoundaryFetchError(null);
    dispatch(setShiftEditabilityDataLoading(true));

    const applyCachedBandBoundaries = () => {
      dispatch(setShiftBoundaries({
        start: shiftStart(selectedDateStr, selectedShift).toISO()!,
        end: shiftEnd(selectedDateStr, selectedShift).toISO()!
      }));
    };

    const applyOfflineEditability = () => {
      const isLatestShift = (latestShiftDate === selectedDateStr && latestShift === selectedShift)
        || (provisionalShiftDate === selectedDateStr && provisionalShift === selectedShift);
      dispatch(setShiftEditability({
        isLocked: !isLatestShift,
        canToggleLock: false,
        isPastAutoLockCutOff: null
      }));
    };

    applyCachedBandBoundaries();

    settingsAjax.getShiftBoundaries(selectedDate, backendCode)
      .then(res => {
        if (!ErrorResponse.isErrorResponse(res)) {
          dispatch(setShiftBoundaries({
            start: res.shiftStart.toISO()!,
            end: res.shiftEnd.toISO()!
          }));
          setShiftBoundaryFetchError(null);
        } else {
          applyCachedBandBoundaries();
          setShiftBoundaryFetchError(res.messages[0].description);
          console.error("Failed to fetch shift boundaries", res);
        }
      })
      .catch(error => {
        if (error !== "CANCELLED") {
          applyCachedBandBoundaries();
          setShiftBoundaryFetchError("Failed to fetch shift boundaries");
          console.error("Failed to fetch shift boundaries", error);
        }
      })
      .then(() => settingsAjax.getShiftEditability(selectedDate, backendCode)
        .then(res => {
          if (!ErrorResponse.isErrorResponse(res)) {
            dispatch(setShiftEditability(res));
            setShiftEditabilityFetchError(null);
            return;
          }
          setShiftEditabilityFetchError(res.messages[0].description);
          console.error("Failed to fetch shift editability", res);
          applyOfflineEditability();
        })
        .catch(() => {
          setShiftEditabilityFetchError("Failed to fetch shift editability");
          applyOfflineEditability();
        })
      )
      .finally(() => {
        dispatch(setShiftEditabilityDataLoading(false));
      });
  }, [dispatch, selectedDateStr, selectedShift, latestShiftDate, latestShift, provisionalShiftDate, provisionalShift, isTimezoneLoading, timezone]);

  return { isTimezoneLoading, shiftBoundaryFetchError, shiftEditabilityFetchError };
}
